import { generateFoodNameSlug } from 'common/FoodUtils';
import { formatCurrency } from 'common/FormatUtilities';
import { formatUrlDate } from 'common/UrlDate';
import { QueryParams } from 'common/urls/QueryParams';
import { Slugs } from 'common/urls/Slugs';
import { useCallback } from 'react';
import { BrowseFilter, CuisineCategoryType } from 'src/gqlReactTypings.generated.d';
import { useSearchActions } from 'src/pages/consumer/components/DishFirstHeader/store';
import { getTopKCuisines } from 'src/shared/design-system/DishFirstCard/utils';
import { ShefCardAvatar } from 'src/shared/design-system/DishFirstShefCard/utils';
import { useHistory } from 'src/shared/hooks/useHistory';
import { useTracker } from 'src/shared/hooks/useTracker';
import { Routes } from 'src/shared/Routes';
import { getLocalTimezone } from 'src/shared/utils/DateTimeUtilities';
import { trackCuisineResultClick, trackDishResultClick, trackShefResultClick } from 'src/shared/utils/EventUtils';
import { getSluggedPath } from 'src/shared/utils/RouteUtilities';
import { RESULT_IMAGE_DIMENSIONS } from './consts';
import { CuisineCardProps, DishCardProps, ShefCardProps } from './types';

export const useDishCard = (props: DishCardProps) => {
  const { dishResult, query } = props;
  const { hide } = useSearchActions();
  const history = useHistory();
  const tracker = useTracker();

  const availableDays = dishResult.availability.filter((a) => a.numAvailable && a.numAvailable > 0);
  const dateParam =
    availableDays.length > 0 ? formatUrlDate(new Date(availableDays[0].availabilityDate), getLocalTimezone()) : '';

  const onClick = useCallback(() => {
    const shefPage = getSluggedPath(Routes.CONSUMER_ORDER_ONE_TIME, [{ slug: Slugs.ID, value: dishResult.userId }], {
      [QueryParams.DATE]: dateParam,
    });
    history.push(shefPage);
    hide();
    trackDishResultClick(tracker, dishResult, query);
  }, [dishResult, dateParam, hide, history, tracker, query]);

  return {
    title: dishResult.name ?? '',
    subtitle: `${formatCurrency(dishResult.price)} · ${getTopKCuisines({ ...dishResult, k: 1 })}`,
    link: getSluggedPath(
      Routes.CONSUMER_ORDER_FOOD_ITEM,
      [
        { slug: Slugs.ID, value: dishResult.userId },
        { slug: Slugs.NAME, value: generateFoodNameSlug(dishResult) || '' },
      ],
      {
        [QueryParams.DATE]: dateParam,
      }
    ),
    onClick,
  };
};

export const useShefCard = (props: ShefCardProps) => {
  const { shefResult, query } = props;
  const { hide } = useSearchActions();
  const tracker = useTracker();

  const onClick = useCallback(() => {
    hide();
    trackShefResultClick(tracker, shefResult, query);
  }, [hide, tracker, shefResult, query]);

  return {
    image: <ShefCardAvatar shef={shefResult} width={RESULT_IMAGE_DIMENSIONS} height={RESULT_IMAGE_DIMENSIONS} />,
    title: shefResult.publicName,
    subtitle: getTopKCuisines({ ...shefResult.shefProfile }),
    link: getSluggedPath(Routes.CONSUMER_ORDER_ONE_TIME, [{ slug: Slugs.ID, value: shefResult.id }]),
    onClick,
  };
};

export const useCuisineCard = (props: CuisineCardProps) => {
  const { cuisineResult, query, zipCode } = props;
  const { hide } = useSearchActions();
  const tracker = useTracker();

  const onClick = useCallback(() => {
    hide();
    trackCuisineResultClick(tracker, cuisineResult, query);
  }, [hide, tracker, cuisineResult, query]);

  const extraSlugs =
    cuisineResult.category.type === CuisineCategoryType.Dietary
      ? {
          [QueryParams.DIETARY_FILTER]: cuisineResult.category.id,
        }
      : {
          [QueryParams.CUISINE_FILTER]: cuisineResult.category.id,
          [QueryParams.BROWSE_FILTER]: BrowseFilter.Cuisine,
        };

  return {
    title: cuisineResult.category.title,
    subtitle: `${cuisineResult.numDishes} dishes`,
    link: getSluggedPath(Routes.CONSUMER_EXPLORE, [{ slug: Slugs.ZIP_CODE, value: zipCode ?? '' }], extraSlugs),
    onClick,
  };
};
